/**
 * Vent - Interactive vent entity for level navigation
 * Allows player to crawl through vents to different locations
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import type { Player } from './Player';

export interface VentConfig {
  id: string;
  entrancePosition: Vector2;
  exitPosition: Vector2;
  size: { width: number; height: number };
  requiresCrouch: boolean;
}

export class Vent extends Entity {
  // Vent properties
  public ventId: string;
  public entrancePosition: Vector2;
  public exitPosition: Vector2;
  public requiresCrouch: boolean;

  // Interaction
  private interactionRadius: number = 50; // pixels
  private isPlayerInVent: boolean = false;
  private crawlSpeed: number = 100; // pixels per second (slower than walking)
  private crawlProgress: number = 0; // 0 = at entrance, 1 = at exit
  private isPlayerNearEntrance: boolean = false;
  private isPlayerNearExit: boolean = false;

  // Visual rendering
  private entranceMesh: THREE.Group | null = null;
  private exitMesh: THREE.Group | null = null;

  constructor(config: VentConfig) {
    super(config.id, 'vent', config.entrancePosition, config.size);

    this.ventId = config.id;
    this.entrancePosition = { ...config.entrancePosition };
    this.exitPosition = { ...config.exitPosition };
    this.requiresCrouch = config.requiresCrouch;

    // Vents don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create visual meshes
    this.createMeshes();
  }

  /**
   * Create Three.js meshes for the vent
   */
  private createMeshes(): void {
    // Create entrance mesh (improved vent grate design)
    this.entranceMesh = new THREE.Group();

    // Dark background
    const entranceGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 1);
    const entranceMaterial = new THREE.MeshBasicMaterial({ color: 0x1a1a1a });
    const entranceBox = new THREE.Mesh(entranceGeometry, entranceMaterial);
    this.entranceMesh.add(entranceBox);

    // Frame border
    const frameThickness = 3;
    const frameGeometry = new THREE.BufferGeometry();
    const framePoints: THREE.Vector3[] = [
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 1),
      new THREE.Vector3(this.size.width / 2, -this.size.height / 2, 1),
      new THREE.Vector3(this.size.width / 2, this.size.height / 2, 1),
      new THREE.Vector3(-this.size.width / 2, this.size.height / 2, 1),
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 1),
    ];
    frameGeometry.setFromPoints(framePoints);
    const frameMaterial = new THREE.LineBasicMaterial({ color: 0x505050, linewidth: frameThickness });
    const frameLine = new THREE.Line(frameGeometry, frameMaterial);
    this.entranceMesh.add(frameLine);

    // Add horizontal grating lines (more lines for better vent look)
    const numLines = 8;
    for (let i = 1; i < numLines; i++) {
      const y = -this.size.height / 2 + (i * this.size.height / numLines);
      const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-this.size.width / 2 + 5, y, 1.5),
        new THREE.Vector3(this.size.width / 2 - 5, y, 1.5),
      ]);
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0x404040 });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.entranceMesh.add(line);
    }

    this.entranceMesh.position.set(this.entrancePosition.x, this.entrancePosition.y, 0);

    // Create exit mesh (same as entrance)
    this.exitMesh = new THREE.Group();

    // Dark background
    const exitGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 1);
    const exitMaterial = new THREE.MeshBasicMaterial({ color: 0x1a1a1a });
    const exitBox = new THREE.Mesh(exitGeometry, exitMaterial);
    this.exitMesh.add(exitBox);

    // Frame border
    const exitFrameGeometry = new THREE.BufferGeometry();
    const exitFramePoints: THREE.Vector3[] = [
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 1),
      new THREE.Vector3(this.size.width / 2, -this.size.height / 2, 1),
      new THREE.Vector3(this.size.width / 2, this.size.height / 2, 1),
      new THREE.Vector3(-this.size.width / 2, this.size.height / 2, 1),
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 1),
    ];
    exitFrameGeometry.setFromPoints(exitFramePoints);
    const exitFrameMaterial = new THREE.LineBasicMaterial({ color: 0x505050, linewidth: frameThickness });
    const exitFrameLine = new THREE.Line(exitFrameGeometry, exitFrameMaterial);
    this.exitMesh.add(exitFrameLine);

    // Add horizontal grating lines
    for (let i = 1; i < numLines; i++) {
      const y = -this.size.height / 2 + (i * this.size.height / numLines);
      const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-this.size.width / 2 + 5, y, 1.5),
        new THREE.Vector3(this.size.width / 2 - 5, y, 1.5),
      ]);
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0x404040 });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.exitMesh.add(line);
    }

    this.exitMesh.position.set(this.exitPosition.x, this.exitPosition.y, 0);
  }

  /**
   * Update vent state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update crawl progress if player is in vent
    if (this.isPlayerInVent) {
      this.updateCrawl(deltaTime);
    }

    // Highlight entrance mesh green when player is nearby
    if (this.entranceMesh) {
      const entranceBox = this.entranceMesh.children[0] as THREE.Mesh;
      if (entranceBox) {
        const material = entranceBox.material as THREE.MeshBasicMaterial;
        material.color.setHex(this.isPlayerNearEntrance ? 0x2a2a2a : 0x1a1a1a);
      }
      const frameLine = this.entranceMesh.children[1] as THREE.Line;
      if (frameLine) {
        const frameMaterial = frameLine.material as THREE.LineBasicMaterial;
        frameMaterial.color.setHex(this.isPlayerNearEntrance ? 0x80ff80 : 0x505050);
      }
    }

    // Highlight exit mesh green when player is nearby
    if (this.exitMesh) {
      const exitBox = this.exitMesh.children[0] as THREE.Mesh;
      if (exitBox) {
        const material = exitBox.material as THREE.MeshBasicMaterial;
        material.color.setHex(this.isPlayerNearExit ? 0x2a2a2a : 0x1a1a1a);
      }
      const frameLine = this.exitMesh.children[1] as THREE.Line;
      if (frameLine) {
        const frameMaterial = frameLine.material as THREE.LineBasicMaterial;
        frameMaterial.color.setHex(this.isPlayerNearExit ? 0x80ff80 : 0x505050);
      }
    }
  }

  /**
   * Render vent (placeholder for Three.js rendering)
   */
  public render(): void {
    // Will be implemented with Three.js sprite rendering
  }

  /**
   * Update crawling through vent
   */
  private updateCrawl(deltaTime: number): void {
    const distance = Math.sqrt(
      Math.pow(this.exitPosition.x - this.entrancePosition.x, 2) +
        Math.pow(this.exitPosition.y - this.entrancePosition.y, 2)
    );

    const progressDelta = (this.crawlSpeed * deltaTime) / distance;
    this.crawlProgress += progressDelta;

    // Check if reached exit
    if (this.crawlProgress >= 1) {
      this.exitVent();
    }
  }

  /**
   * Check if player can interact with vent entrance
   */
  public canInteractWithEntrance(player: Player): boolean {
    if (this.destroyed || !this.active || this.isPlayerInVent) {
      return false;
    }

    const distance = this.distanceTo(player);
    return distance <= this.interactionRadius;
  }

  /**
   * Check if player can interact with vent exit
   */
  public canInteractWithExit(player: Player): boolean {
    if (this.destroyed || !this.active) {
      return false;
    }

    // Calculate distance to exit position
    const dx = player.position.x - this.exitPosition.x;
    const dy = player.position.y - this.exitPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance <= this.interactionRadius;
  }

  /**
   * Enter the vent
   */
  public enter(player: Player): boolean {
    if (!this.canInteractWithEntrance(player)) {
      return false;
    }

    if (this.requiresCrouch && !player.isPlayerCrouching()) {
      console.log(`Vent '${this.ventId}' requires crouching`);
      return false;
    }

    this.isPlayerInVent = true;
    this.crawlProgress = 0;
    console.log(`Player entered vent '${this.ventId}'`);

    return true;
  }

  /**
   * Exit the vent
   */
  private exitVent(): void {
    this.isPlayerInVent = false;
    this.crawlProgress = 0;
    console.log(`Player exited vent '${this.ventId}'`);

    // Event for moving player to exit position will be handled by level
  }

  /**
   * Force exit (if player cancels crawl)
   */
  public forceExit(): void {
    if (!this.isPlayerInVent) {
      return;
    }

    // Return player to entrance or exit based on progress
    if (this.crawlProgress < 0.5) {
      console.log(`Player returned to entrance of vent '${this.ventId}'`);
    } else {
      console.log(`Player continued to exit of vent '${this.ventId}'`);
    }

    this.isPlayerInVent = false;
    this.crawlProgress = 0;
  }

  /**
   * Get current crawl position
   */
  public getCurrentPosition(): Vector2 {
    if (!this.isPlayerInVent) {
      return { ...this.entrancePosition };
    }

    // Interpolate between entrance and exit
    return {
      x: this.entrancePosition.x + (this.exitPosition.x - this.entrancePosition.x) * this.crawlProgress,
      y: this.entrancePosition.y + (this.exitPosition.y - this.entrancePosition.y) * this.crawlProgress,
    };
  }

  /**
   * Get vent length
   */
  public getLength(): number {
    return Math.sqrt(
      Math.pow(this.exitPosition.x - this.entrancePosition.x, 2) +
        Math.pow(this.exitPosition.y - this.entrancePosition.y, 2)
    );
  }

  /**
   * Get estimated crawl time
   */
  public getEstimatedCrawlTime(): number {
    return this.getLength() / this.crawlSpeed;
  }

  // Getters
  public getIsPlayerInVent(): boolean {
    return this.isPlayerInVent;
  }

  public getCrawlProgress(): number {
    return this.crawlProgress;
  }

  public getExitPosition(): Vector2 {
    return { ...this.exitPosition };
  }

  public getEntrancePosition(): Vector2 {
    return { ...this.entrancePosition };
  }

  public getRequiresCrouch(): boolean {
    return this.requiresCrouch;
  }

  public setPlayerNearEntrance(nearby: boolean): void {
    this.isPlayerNearEntrance = nearby;
  }

  public setPlayerNearExit(nearby: boolean): void {
    this.isPlayerNearExit = nearby;
  }

  public setPlayerNearby(nearby: boolean): void {
    this.isPlayerNearEntrance = nearby;
  }

  public getMeshes(): { entrance: THREE.Group | null; exit: THREE.Group | null } {
    return {
      entrance: this.entranceMesh,
      exit: this.exitMesh,
    };
  }

  /**
   * Get vent info for UI/debugging
   */
  public getInfo(): {
    id: string;
    length: number;
    requiresCrouch: boolean;
    playerInside: boolean;
    progress: number;
  } {
    return {
      id: this.ventId,
      length: this.getLength(),
      requiresCrouch: this.requiresCrouch,
      playerInside: this.isPlayerInVent,
      progress: this.crawlProgress,
    };
  }

  /**
   * Clean up
   */
  public override destroy(): void {
    // Clean up meshes
    if (this.entranceMesh) {
      this.entranceMesh.children.forEach(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        } else if (child instanceof THREE.Line) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    }

    if (this.exitMesh) {
      this.exitMesh.children.forEach(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        } else if (child instanceof THREE.Line) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    }

    super.destroy();
  }
}
